// Dependencies
import axios from 'axios'
import { omitBy, isNil } from 'lodash'

// Services
import { accessHeaders } from '@/lib/cookie'

// Utils
import {
  ConvertUtility as Convert
} from '../utils/convert-utility'

import {
  ClientUtility as Client
} from '../utils/client-utility'

/**
 * Subscriber report search parameters
 * @typedef SubsReportSearchParams
 * @type {Object}
 * @property {String[]} [docs]         - Document numbers
 * @property {String} [dateType]       - Date filter type
 * @property {Date|String} [dateStart] - Period start
 * @property {Date|String} [dateStop]  - Period stop
 * @property {String} [status]         - Subscription status
 * @property {String} [pkg]            - Package type
 * @property {String} [receipt]        - Receipt type
 * @property {String} [seller]         - Seller
 * @property {Boolean} [usedCoin]      - Used MNC flag
 * @property {String} [groupName]      - Group name
 * @property {Number} [projectId]      - Project ID
 * @property {Number} [page]           - Fetch page
 * @property {Number} [limit]          - Fetch limit
 * @property {Boolean} [incentiveflag] - Include incentive-gated columns (export only)
 */

/**
 * @typedef SubsReportUser
 * @type {Object}
 * @property {String|null} userId
 * @property {String|null} name
 * @property {String|null} gender
 * @property {String|null} mobile
 * @property {Number|null} birthYear
 * @property {String|null} taxId
 */

/**
 * @typedef SubsReportSubscription
 * @type {Object}
 * @property {String|null} subscriptionId
 * @property {String|null} orderNo
 * @property {String} projectName
 * @property {String} address
 * @property {String[]} category
 * @property {Number|null} packageId
 * @property {String|null} packageName
 * @property {String|null} seller
 * @property {String|null} type
 * @property {String|null} txnStatus
 * @property {String|null} billingStatus
 * @property {String|null} actualStatus
 */

/**
 * @typedef SubsReportPricing
 * @type {Object}
 * @property {String|null} paymentDate
 * @property {String|null} paymentTime
 * @property {String|null} dateStart
 * @property {String|null} dateEnd
 * @property {String|null} optMain
 * @property {String|null} optExtra
 * @property {Number|null} optMainPrice
 * @property {Number|null} optExtraPrice
 * @property {Number|null} quantity
 * @property {String|null} discountTitle
 * @property {Number|null} discountAmount
 * @property {Number|null} vat
 * @property {Number|null} mdrAndVat
 * @property {Number|null} feeRate
 * @property {String|null} feeRateText
 * @property {Number|null} sellingPriceAfterVat
 * @property {Number|null} sellingPriceBeforeVat
 * @property {Number|null} netIncomeBeforeMdr
 * @property {Number|null} netFee
 * @property {Number|null} netIncome
 * @property {Number|null} netSellingPriceAfterVat
 */

/**
 * @typedef SubsReportMnc
 * @type {Object}
 * @property {Number|null} coinAmount
 * @property {Number|null} coinValue
 * @property {String|null} coinWallet
 */

/**
 * @typedef SubsReportReceiptInfo
 * @type {Object}
 * @property {String|null} name
 * @property {String|null} address
 * @property {String|null} taxId
 * @property {String|null} branchNo
 */

/**
 * @typedef SubsReportReceipt
 * @type {Object}
 * @property {String|null} receiptId
 * @property {String|null} creditNoteId
 * @property {String|null} creditNoteNo
 * @property {String|null} paymentChannel
 * @property {String|null} imageTransfer
 * @property {SubsReportReceiptInfo|null} receiptInfo
 * @property {String|null} receiptNo
 * @property {String|null} refCode
 * @property {String|null} note
 */

/**
 * @typedef SubsReportService
 * @type {Object}
 * @property {Number|null} id
 * @property {String|null} name
 * @property {String|null} providerName
 * @property {Number|null} costBeforeVat
 * @property {Number|null} costAfterVat
 */

/**
 * @typedef SubsReportAppointment
 * @type {Object}
 * @property {String|null} appointmentId
 * @property {String|null} bookedAt
 * @property {String|null} performedAt
 * @property {String|null} performedTime
 * @property {String|null} confirmedAt
 * @property {String|null} usedAt
 * @property {String|null} closedAt
 * @property {String|null} providerName
 * @property {Number|null} actualCost
 * @property {String|null} maintNo
 * @property {String|null} status
 * @property {Boolean|null} hasRemark
 */

/**
 * @typedef SubsReportSap
 * @type {Object}
 * @property {String|null} providerName
 * @property {String|null} serviceType
 * @property {String|null} vendorReceiptDate
 * @property {String|null} vendorReceiptNo
 * @property {String|null} purchaseOrder
 * @property {Number|null} price
 */

/**
 * Subscriber report project
 * @typedef SubsReportProject
 * @type {Object}
 * @property {String} projectId
 * @property {String} projectCode
 * @property {String} projectName
 * @property {String|null} addressRoad
 * @property {String|null} amphurName
 * @property {String|null} latitude
 * @property {String|null} longitude
 * @property {String|null} moo
 * @property {String|null} preAmphur
 * @property {String|null} preTumbon
 * @property {String|null} projectCover
 * @property {String|null} projectGroup
 * @property {String|null} projectIcon
 * @property {String|null} projectLocation
 * @property {String|null} projectSaleStatus
 * @property {String|null} projectTabletCover
 * @property {String|null} projectType
 * @property {String|null} provinceName
 * @property {String|null} soi
 * @property {String|null} tumbonName
 * @property {String|null} zipCode
 */

/**
 * Subscriber report record
 * @typedef SubsReportItem
 * @type {Object}
 * @property {SubsReportUser} user
 * @property {SubsReportSubscription} subscription
 * @property {SubsReportPricing} pricing
 * @property {SubsReportMnc} mnc
 * @property {SubsReportReceipt} receipt
 * @property {SubsReportService} service
 * @property {SubsReportAppointment} appointment
 * @property {SubsReportSap} sap
 */

/**
 * Subscriber report list result
 * @typedef SubsReportList
 * @type {Object}
 * @property {Number} total
 * @property {SubsReportItem[]} items
 */

/**
 * @typedef SubsReportSummarySubs
 * @type {Object}
 * @property {Number} total
 * @property {Number} subscribed
 * @property {Number} cancelled
 * @property {Number} unsubscribed
 * @property {Number} expired
 */

/**
 * Subscriber report summary result
 * @typedef SubsReportSummary
 * @type {Object}
 * @property {Number} total    - Appointment row count
 * @property {Number} users    - Unique user count
 * @property {SubsReportSummarySubs} subs
 * @property {Number} services - Service count
 */

export class SubscriberReportClient {

  // Legacy admin API URL
  static legacy = (
    `${process.env.HOST}/api/admin`
  )

  // RueJai admin API URL
  static endpoint = (
    `${process.env.ADMIN_HOST}/ruejai-backend-admin/api/report/subscriber`
  )

  // Cached project list
  static projectsCache = null

  /**
   * Request project list, cached after first success
   * @returns {Promise<SubsReportProject[]>} Project list
   */
  static async projects() {

    // Return Cache
    if (this.projectsCache) return this.projectsCache

    // Request URL
    const url = `${this.legacy}/projects`

    // Default Result
    let result = []

    try {

      // Request Headers
      const headers = accessHeaders()

      // Request Project List
      const { data } = await axios.get(
        url, { headers }
      )

      // Validate Result
      if (Array.isArray(data)) {
        result = data
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'subscriber-report.projects', error
      )

      // Execute Error
      throw new Error(
        errorCode || message
      )
    }

    // Set Cache
    this.projectsCache = result

    return result
  }


  /**
   * Request subscriber report list
   * @param {SubsReportSearchParams} [params] - Search parameters
   * @returns {Promise<SubsReportList>} Report list
   */
  static async list(params = {}) {

    // Request URL
    const url = `${this.endpoint}/list`

    // Default Result
    let result = {
      total: 0, items: []
    }

    try {

      // Request Headers
      const headers = accessHeaders()

      // Request Report List
      const { data } = await axios.get(
        url, {
          headers, params: omitBy(params, isNil)
        }
      )

      // Validate Result
      if (Client.has(data) && data.result && typeof data.result === 'object') {
        result = Convert.key(data.result)
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'subscriber-report.list', error
      )

      // Execute Error
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request subscriber report summary
   * @param {SubsReportSearchParams} [params] - Search parameters
   * @returns {Promise<SubsReportSummary>} Report summary
   */
  static async summary(params = {}) {

    // Request URL
    const url = `${this.endpoint}/summary`

    // Default subs total
    const subs = {
      total: 0, subscribed: 0, cancelled: 0, unsubscribed: 0, expired: 0
    }

    // Default Result
    let result = {
      total: 0, users: 0, services: 0, subs
    }

    try {

      // Request Headers
      const headers = accessHeaders()

      // Request Report Summary
      const { data } = await axios.get(
        url, {
          headers, params: omitBy(params, isNil)
        }
      )

      // Result validation
      const valid = (
        Client.has(data) && data.result && typeof data.result === 'object'
      )

      // Validate Result
      if (valid) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'subscriber-report.summary', error
      )

      // Execute Error
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request subscriber report export file
   * @param {SubsReportSearchParams} [params] - Search parameters
   * @returns {Promise<Blob>} Report export file
   */
  static async export(params = {}) {

    // Request URL
    const url = `${this.endpoint}/export`

    try {

      // Request Headers
      const headers = {
        ...accessHeaders(), Accept: 'application/octet-stream'
      }

      // Request Report Export
      const { data } = await axios.get(
        url, {
          headers, params: omitBy(params, isNil), responseType: 'blob'
        }
      )

      return data
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'subscriber-report.export', error
      )

      // Execute Error
      throw new Error(
        errorCode || message
      )
    }
  }

}